import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Search, Eye, Phone, Stamp, Plus, Wrench,
  Loader2, Calendar, Building2, AlertTriangle
} from 'lucide-react';
import api from '../../utils/api';

const MobileStamping = () => {
  const [licenses, setLicenses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createServiceDialogOpen, setCreateServiceDialogOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Create form state
  const [formData, setFormData] = useState({
    customer_id: '',
    machine_device: '',
    license_type: '',
    quantity: 1,
    start_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    renewal_due_date: ''
  });

  // Service creation form
  const [serviceForm, setServiceForm] = useState({
    customer_id: '',
    service_type: 'Stamping/License Renewal Service',
    issue_description: '',
    priority: 'Medium',
    assigned_to: '',
    due_date: ''
  });

  const licenseTypes = ['Boiler License', 'Factory License', 'Fire NOC', 'Pollution Control', 'Trade License', 'FSSAI License', 'Other'];

  const fetchLicenses = useCallback(async () => {
    try {
      const response = await api.get('/licenses/');
      setLicenses(response.data);
    } catch (error) {
      toast.error('Failed to fetch licenses');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await api.get('/customers/');
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  }, []);

  const fetchEngineers = useCallback(async () => {
    try {
      const response = await api.get('/employees/');
      const engineersList = response.data.filter(e => 
        e.status === 'Active' && 
        (e.role === 'Service Engineer' || e.role === 'BDM & SE')
      );
      setEngineers(engineersList);
    } catch (error) {
      console.error('Failed to fetch engineers:', error);
    }
  }, []);

  useEffect(() => {
    fetchLicenses();
    fetchCustomers();
    fetchEngineers();
  }, [fetchLicenses, fetchCustomers, fetchEngineers]);

  // Helper functions
  const getDaysUntilRenewal = (expiryDate) => {
    if (!expiryDate) return null;
    const expiry = new Date(expiryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    const diffTime = expiry - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getStatus = (expiryDate) => {
    const days = getDaysUntilRenewal(expiryDate);
    if (days === null) return { label: 'Unknown', color: 'bg-gray-500 text-white' };
    if (days < 0) return { label: 'Expired', color: 'bg-red-500 text-white' };
    if (days <= 30) return { label: 'Expiring Soon', color: 'bg-orange-500 text-white' };
    if (days <= 60) return { label: 'Due Soon', color: 'bg-yellow-500 text-white' };
    return { label: 'Valid', color: 'bg-green-500 text-white' };
  };

  // Filter licenses
  const filteredLicenses = licenses.filter(license => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      license.license_no?.toLowerCase().includes(searchLower) ||
      license.customer?.company_name?.toLowerCase().includes(searchLower) ||
      license.customer?.mobile?.includes(searchTerm) ||
      license.machine_device?.toLowerCase().includes(searchLower) ||
      license.license_type?.toLowerCase().includes(searchLower);
    
    const status = getStatus(license.expiry_date);
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'expired' && status.label === 'Expired') ||
      (statusFilter === 'expiring' && (status.label === 'Expiring Soon' || status.label === 'Due Soon')) ||
      (statusFilter === 'valid' && status.label === 'Valid');
    
    return matchesSearch && matchesStatus;
  });

  // Sort by expiry date (earliest first)
  const sortedLicenses = [...filteredLicenses].sort((a, b) => {
    const daysA = getDaysUntilRenewal(a.expiry_date) || 9999;
    const daysB = getDaysUntilRenewal(b.expiry_date) || 9999;
    return daysA - daysB;
  });

  const handleOpenCreate = () => {
    setFormData({
      customer_id: '',
      machine_device: '',
      license_type: '',
      quantity: 1,
      start_date: new Date().toISOString().split('T')[0],
      expiry_date: '',
      renewal_due_date: ''
    });
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!formData.license_type) {
      toast.error('Please select license type');
      return;
    }
    if (!formData.expiry_date) {
      toast.error('Please enter expiry date');
      return;
    }

    // Auto-calculate renewal_due_date if not set (30 days before expiry)
    let renewalDate = formData.renewal_due_date;
    if (!renewalDate && formData.expiry_date) {
      const expiry = new Date(formData.expiry_date);
      expiry.setDate(expiry.getDate() - 30);
      renewalDate = expiry.toISOString().split('T')[0];
    }

    setSubmitting(true);
    try {
      await api.post('/licenses/', {
        ...formData,
        renewal_due_date: renewalDate
      });
      toast.success('License created successfully');
      setCreateDialogOpen(false);
      fetchLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create license');
    } finally {
      setSubmitting(false);
    }
  };

  // Open Create Service dialog for a license
  const handleOpenCreateService = (license) => {
    setSelectedLicense(license);
    const days = getDaysUntilRenewal(license.renewal_due_date || license.expiry_date);
    
    setServiceForm({
      customer_id: license.customer_id || '',
      service_type: 'Stamping/License Renewal Service',
      issue_description: `Stamping/License Renewal Service\n\nLicense No: ${license.license_no}\nMachine/Device: ${license.machine_device}\nLicense Type: ${license.license_type}\nQuantity: ${license.quantity || 1} devices\nRenewal Due: ${license.renewal_due_date ? new Date(license.renewal_due_date).toLocaleDateString('en-IN') : 'N/A'}${days !== null ? ` (${days < 0 ? Math.abs(days) + ' days overdue' : days + ' days left'})` : ''}\n\nCustomer: ${license.customer?.company_name || 'N/A'}`,
      priority: days !== null && days <= 30 ? 'High' : 'Medium',
      assigned_to: '',
      due_date: license.renewal_due_date || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    });
    
    setCreateServiceDialogOpen(true);
  };

  // Create service from stamping
  const handleCreateService = async () => {
    if (!serviceForm.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!serviceForm.issue_description) {
      toast.error('Issue description is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/services/', {
        customer_id: serviceForm.customer_id,
        service_type: serviceForm.service_type,
        description: serviceForm.issue_description,
        priority: serviceForm.priority,
        assigned_to: serviceForm.assigned_to || null,
        due_date: serviceForm.due_date || null,
        license_id: selectedLicense?.id || null
      });

      const createdService = response.data;
      
      // Update the license with service reference
      await api.put(`/licenses/${selectedLicense.id}`, {
        ...selectedLicense,
        service_reference: createdService.service_no,
        service_notes: `Service created: ${createdService.service_no} on ${new Date().toLocaleDateString('en-IN')}`
      });

      toast.success(`Service ${createdService.service_no} created`);
      setCreateServiceDialogOpen(false);
      fetchLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create service');
    } finally {
      setSubmitting(false);
    }
  };

  // Check if license is due within 30 days and doesn't have a service
  const canCreateService = (license) => {
    const days = getDaysUntilRenewal(license.renewal_due_date || license.expiry_date);
    return days !== null && days <= 30 && !license.service_reference;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-pink-600" />
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Stamping / Licenses</h1>
          <p className="text-sm text-gray-500">{filteredLicenses.length} licenses</p>
        </div>
        <Button size="sm" onClick={handleOpenCreate}>
          <Plus className="w-4 h-4 mr-1" /> Add New
        </Button>
      </div>

      {/* Search & Filters */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search license, customer, mobile..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger>
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="expiring">Expiring Soon</SelectItem>
            <SelectItem value="valid">Valid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Licenses List */}
      {sortedLicenses.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <Stamp className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No licenses found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {sortedLicenses.map((license) => {
            const days = getDaysUntilRenewal(license.expiry_date);
            const status = getStatus(license.expiry_date);
            
            return (
              <Card 
                key={license.id} 
                className={`overflow-hidden ${days !== null && days <= 30 ? 'border-orange-300 bg-orange-50' : days !== null && days < 0 ? 'border-red-300 bg-red-50' : ''}`}
              >
                <CardContent className="p-4">
                  {/* Header Row */}
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <span className="text-sm font-semibold text-pink-600">{license.license_no}</span>
                      <Badge className={`ml-2 ${status.color}`}>
                        {status.label}
                      </Badge>
                    </div>
                    {days !== null && days <= 30 && (
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                    )}
                  </div>

                  {/* Customer Info */}
                  <div className="space-y-2 mb-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{license.customer?.company_name}</span>
                    </div>
                    {license.customer?.mobile && (
                      <a href={`tel:${license.customer.mobile}`} className="flex items-center gap-2 text-blue-600">
                        <Phone className="w-4 h-4" />
                        <span>{license.customer.mobile}</span>
                      </a>
                    )}
                  </div>

                  {/* License Details */}
                  <div className="bg-gray-50 rounded-lg p-2 mb-3 text-sm">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-gray-500">Type:</span>
                      <span className="font-medium">{license.license_type}</span>
                    </div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-gray-500">Device:</span>
                      <span className="font-medium">{license.machine_device || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Expiry:</span>
                      <span className={`font-medium ${days !== null && days <= 0 ? 'text-red-600' : days !== null && days <= 30 ? 'text-orange-600' : 'text-green-600'}`}>
                        {license.expiry_date ? new Date(license.expiry_date).toLocaleDateString('en-IN') : 'N/A'}
                        {days !== null && <span className="ml-1">({days > 0 ? `${days}d left` : days === 0 ? 'Today' : `${Math.abs(days)}d overdue`})</span>}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="flex-1"
                      onClick={() => { setSelectedLicense(license); setViewDialogOpen(true); }}
                    >
                      <Eye className="w-4 h-4 mr-1" /> View
                    </Button>
                    {canCreateService(license) && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleOpenCreateService(license)}
                        className="text-blue-600 border-blue-200"
                      >
                        <Wrench className="w-4 h-4 mr-1" /> Service
                      </Button>
                    )}
                    {license.service_reference && (
                      <Badge className="bg-green-100 text-green-800 text-xs">
                        {license.service_reference}
                      </Badge>
                    )}
                    {license.customer?.mobile && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        asChild
                      >
                        <a href={`tel:${license.customer.mobile}`}>
                          <Phone className="w-4 h-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* View License Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>License Details - {selectedLicense?.license_no}</DialogTitle>
          </DialogHeader>
          {selectedLicense && (
            <div className="space-y-4">
              <Badge className={getStatus(selectedLicense.expiry_date).color}>
                {getStatus(selectedLicense.expiry_date).label}
              </Badge>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Customer</p>
                  <p className="font-medium">{selectedLicense.customer?.company_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Mobile</p>
                  <p className="font-medium">{selectedLicense.customer?.mobile || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">License Type</p>
                  <p className="font-medium">{selectedLicense.license_type}</p>
                </div>
                <div>
                  <p className="text-gray-500">Machine/Device</p>
                  <p className="font-medium">{selectedLicense.machine_device || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Quantity</p>
                  <p className="font-medium">{selectedLicense.quantity || 1}</p>
                </div>
                <div>
                  <p className="text-gray-500">Start Date</p>
                  <p className="font-medium">{selectedLicense.start_date ? new Date(selectedLicense.start_date).toLocaleDateString('en-IN') : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Expiry Date</p>
                  <p className="font-medium">{selectedLicense.expiry_date ? new Date(selectedLicense.expiry_date).toLocaleDateString('en-IN') : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Created</p>
                  <p className="font-medium">{new Date(selectedLicense.created_at).toLocaleDateString('en-IN')}</p>
                </div>
              </div>
              
              {selectedLicense.notes && (
                <div>
                  <p className="text-gray-500 text-sm">Notes</p>
                  <p className="text-sm mt-1">{selectedLicense.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create License Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New License</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSubmit}>
            <div className="space-y-4">
              {/* Customer Selection */}
              <div>
                <Label>Customer *</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(v) => setFormData({ ...formData, customer_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.company_name} - {customer.mobile}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* License Type */}
              <div>
                <Label>License Type *</Label>
                <Select
                  value={formData.license_type}
                  onValueChange={(v) => setFormData({ ...formData, license_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {licenseTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Machine/Device */}
              <div>
                <Label>Machine/Device *</Label>
                <Input
                  value={formData.machine_device}
                  onChange={(e) => setFormData({ ...formData, machine_device: e.target.value })}
                  placeholder="Enter machine or device name"
                />
              </div>

              {/* Quantity */}
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Expiry Date *</Label>
                  <Input
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  />
                </div>
              </div>

              {/* Renewal Due Date (optional) */}
              <div>
                <Label>Renewal Due Date</Label>
                <Input
                  type="date"
                  value={formData.renewal_due_date}
                  onChange={(e) => setFormData({ ...formData, renewal_due_date: e.target.value })}
                  placeholder="Auto-calculated if empty"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty to auto-set 30 days before expiry</p>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create License'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Service Dialog */}
      <Dialog open={createServiceDialogOpen} onOpenChange={setCreateServiceDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5" />
              Create Service for License Renewal
            </DialogTitle>
          </DialogHeader>
          
          {selectedLicense && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <p className="text-sm font-medium text-amber-800">
                License: {selectedLicense.license_no}
              </p>
              <p className="text-xs text-amber-700">
                {selectedLicense.machine_device} | Qty: {selectedLicense.quantity || 1} | 
                Due: {selectedLicense.renewal_due_date ? new Date(selectedLicense.renewal_due_date).toLocaleDateString('en-IN') : 'N/A'}
              </p>
            </div>
          )}
          
          <div className="space-y-4">
            {/* Customer */}
            <div>
              <Label>Customer *</Label>
              <Select
                value={serviceForm.customer_id}
                onValueChange={(v) => setServiceForm(prev => ({ ...prev, customer_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.company_name} - {c.mobile}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Service Type & Priority */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Service Type</Label>
                <Select
                  value={serviceForm.service_type}
                  onValueChange={(v) => setServiceForm(prev => ({ ...prev, service_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Stamping/License Renewal Service">License Renewal</SelectItem>
                    <SelectItem value="Onsite Field Service">Onsite Field</SelectItem>
                    <SelectItem value="In-Office Service">In-Office</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select
                  value={serviceForm.priority}
                  onValueChange={(v) => setServiceForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Engineer & Due Date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Assign To</Label>
                <Select
                  value={serviceForm.assigned_to || 'none'}
                  onValueChange={(v) => setServiceForm(prev => ({ ...prev, assigned_to: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select engineer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {engineers.map(e => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={serviceForm.due_date}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, due_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            
            {/* Description */}
            <div>
              <Label>Issue Description *</Label>
              <Textarea
                value={serviceForm.issue_description}
                onChange={(e) => setServiceForm(prev => ({ ...prev, issue_description: e.target.value }))}
                rows={4}
                placeholder="Service details..."
              />
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCreateServiceDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateService} disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Service'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileStamping;
